/*
 * Generates the table of assets from data retrieved from the API.
 */

import React from "react";
import { Asset } from "../pages/AssetsPage";
import { dateDisplay, companyDisplay, DATE_FORMAT } from "../helpers/dataHelper";
import moment from "moment-timezone";
import MUIDataTable from "mui-datatables";
import clsx from "clsx";
import { Typography } from "@material-ui/core";
import useRowStyles from "../hooks/useRowStyles";
interface AssetTableProps {
    assets: Asset[];
    paging: {
        page: number;
        perPage: number;
        total: number;
        setPage: (page: number) => void;
        setRowsPerPage: (numberOfRows: number) => void;
    };
    sorting: {
        sortProperty: string;
        sortDirection: number;
        setSort: (property: string) => void;
    };
}

const AssetsTable: React.FC<AssetTableProps> = ({ assets, paging, sorting }) => {
    const rowClasses = useRowStyles();
    const columns = [
        {
            label: "Asset Tag",
            name: "tag",
            options: {
                customHeadLabelRender: (columnMeta: any) => {
                    return (
                        <Typography component={"span"} noWrap={true} variant="body2">
                            {columnMeta.label}
                        </Typography>
                    );
                }
            }
        },
        {
            label: "Company Name",
            name: "client",
            options: {
                customBodyRenderLite: (dataIndex: number) => {
                    const asset = assets[dataIndex];
                    return companyDisplay(asset.client);
                }
            }
        },
        {
            label: "Location",
            name: "location"
        },
        {
            label: "Hostname",
            name: "hostname"
        },
        {
            label: "IP Address",
            name: "ipAddress"
        },
        {
            label: "Asset Type",
            name: "type"
        },
        {
            label: "Vendor",
            name: "vendor"
        },
        {
            label: "Product",
            name: "product"
        },
        {
            label: "WarrantyExpiry",
            name: "warrantyExpiry",
            options: { customBodyRender: (value: any) => dateDisplay(value) }
        },
        {
            label: "URL",
            name: "url",
            options: {
                display: false
            }
        }
    ];

    return (
        <MUIDataTable
            title=""
            data={assets}
            columns={columns}
            options={{
                sortOrder: {
                    name: sorting.sortProperty,
                    direction: sorting.sortDirection === -1 ? "desc" : "asc"
                },
                viewColumns: false,
                print: false,
                filter: false,
                search: false,
                selectableRows: "none",
                serverSide: true,
                page: paging.page,
                rowsPerPage: paging.perPage ?? 30,
                count: paging.total,
                downloadOptions: {
                    filename: `AssetsTable${moment().format("YYYYMMDDhhmm")}.csv`
                },
                onColumnSortChange: (changedColumn: string) => {
                    sorting.setSort(changedColumn);
                },
                onChangeRowsPerPage: (numberOfRows: number) => {
                    paging.setRowsPerPage(numberOfRows);
                },
                onChangePage: (currentPage: number) => {
                    paging.setPage(currentPage);
                },
                setTableProps: () => {
                    return {
                        size: "small"
                    };
                },
                setRowProps: (row: any[]) => {
                    const overdueStatus = () => {
                        const timeToDueDate = moment.utc(row[9], DATE_FORMAT).diff(moment.utc());
                        if (timeToDueDate < 0) {
                            return "overdue";
                        } else if (timeToDueDate < 7200000) {
                            // 2 hours
                            return "close";
                        }

                        return "ok";
                    };

                    return {
                        className: clsx(
                            overdueStatus() === "overdue" && rowClasses.overdue,
                            overdueStatus() === "close" && rowClasses.close,
                            overdueStatus() === "ok" && rowClasses.standard
                        )
                    };
                },
                onRowClick: (rowData: string[]) => {
                    window.open(`${rowData[9]}`);
                },
                rowsPerPageOptions: [30, 100, 300]
            }}
        />
    );
};

export default AssetsTable;
